
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { ReactNode } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/nextjs';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { SpeedInsights as VercelSpeedInsights } from '@vercel/speed-insights/next';
import { once } from 'lodash';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import useTranslation from 'next-translate/useTranslation';
import { ConsentedTrackingScripts } from 'components/CookieConsent/ConsentedTrackingScripts';
import { CookieConsentBanner } from 'components/CookieConsent/CookieConsentBanner';
import { ErrorFallback } from 'components/Error/ErrorFallback';
import { CookieConsentStatus, CookieConsentStorageKey, } from 'constants/localStorage';
import ChooseMultipleWinnersProvider from 'providers/ChooseMultipleWinnersProvider';
import CookieConsentProvider from 'providers/CookieConsentProvider';
import CustomApolloProvider from 'providers/CustomApolloProvider';
import { GlobalToasts } from 'providers/GlobalToasts';
import LoadingProvider from 'providers/LoadingProvider';
import RegionProvider from 'providers/RegionProvider';
import UserProvider from 'providers/UserProvider';
import { theme } from 'theme';
import { Fonts } from 'theme/Fonts';
import { GOOGLE_ANALYTICS_MEASUREMENT_ID, isGoogleAnalyticsEnabled, } from 'utils/analytics';
import { YupConfig } from 'yup.config';
const initialize = once(() => {
    YupConfig.configure();
});
const App = ({ Component, pageProps }: AppProps) => {
    initialize();
    const { t } = useTranslation('common');
    const getLayout = Component.getLayout || ((page: ReactNode) => page);
    const pageTitle = `EzTenda | ${t('VENUE_PAGE_TEXT5')}`;
    return (<>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <ChakraProvider theme={theme}>
        <GlobalToasts>
          <Fonts />
          <CustomApolloProvider pageProps={pageProps}>
            <UserProvider>
              {/*
        TODO: rework multiple bids context to general store if needed
      */}
              <RegionProvider>
                <LoadingProvider>
                  <ChooseMultipleWinnersProvider>
                    <CookieConsentProvider>
                      {/*
        Runs first, so Google Consent Mode defaults are set
        before any Google tag loads
      */}
                      <Script id="datalayer" dangerouslySetInnerHTML={{
            __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        var storedCookieConsentStatus = null;
                        try {
                          storedCookieConsentStatus = localStorage.getItem('${CookieConsentStorageKey}');
                        } catch (error) {}
                        var consentModeStatus = storedCookieConsentStatus === '${CookieConsentStatus.Granted}' ? 'granted' : 'denied';
                        gtag('consent', 'default', {
                          analytics_storage: consentModeStatus,
                          ad_storage: consentModeStatus,
                          ad_user_data: consentModeStatus,
                          ad_personalization: consentModeStatus
                        });
                      `,
        }}/>
                      {isGoogleAnalyticsEnabled && (<>
                          {/* GOOGLE ANALYTICS 4, cookieless until consent is granted */}
                          <Script id="google-analytics-library" strategy="afterInteractive" src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`}/>
                          <Script id="google-analytics" strategy="afterInteractive" dangerouslySetInnerHTML={{
                __html: `
                        gtag('js', new Date());
                        gtag('config', '${GOOGLE_ANALYTICS_MEASUREMENT_ID}');
                    `,
            }}/>
                        </>)}
                      <ConsentedTrackingScripts />
                      <Sentry.ErrorBoundary 
    // eslint-disable-next-line react/no-unstable-nested-components
    fallback={({ resetError }) => (<ErrorFallback resetError={resetError}/>)}>
                        {getLayout(<Component {...pageProps}/>)}
                      </Sentry.ErrorBoundary>
                      <CookieConsentBanner />
                    </CookieConsentProvider>
                  </ChooseMultipleWinnersProvider>
                </LoadingProvider>
              </RegionProvider>
            </UserProvider>
          </CustomApolloProvider>
        </GlobalToasts>
      </ChakraProvider>
      <VercelAnalytics />
      <VercelSpeedInsights />
    </>);
};
const __Next_Translate__Page__1a17794ccf4__ = App;

    export default __appWithI18n(__Next_Translate__Page__1a17794ccf4__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  